// controller-plan-index-markdown.ts
//
// Phase 06e R5 (F-25 consequence, spec E24). Every ticket's `plan_path` names
// `.avatar/PLAN.md` (seed, redirect decision, review), but nothing wrote it:
// the plan ticket has `allowed_paths: []`, so its worker can only write the
// staging dir, which is never committed. The controller renders it instead —
// an index of the DAG built ONLY from validated ticket fields (never worker
// prose), after a proposal batch touched tickets, committed with them.
//
// Deterministic by construction: sorted by id, one section per `slice_id`
// (ordered by its smallest id), no status, no time. Retired tickets and
// controller-reserved ids (review sessions) are left out. Every cell is one
// line, clipped, redacted (pattern SecretRedactor) and `|`-escaped. Not
// hashed by G2 (g2-plan-content-snapshot.ts): the tickets it derives from are.
import { PLAN_INDEX_PATH } from "./controller-owned-paths.js";
import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import { writeControllerWorktreeFile } from "./controller-worktree-safe-file-write.js";
import { RETIRED_TICKET_STATUSES } from "./g2-plan-readiness.js";
import { redactEvidence } from "./retry-policy-evidence.js";
import type { Ticket } from "./schemas/ticket-schema.js";

// 06f R1: declared in controller-owned-paths.ts (the path is controller-owned); re-exported for callers.
export { PLAN_INDEX_PATH };

const HEADER =
  "<!-- Sinh bởi controller từ .avatar/tickets/ — không sửa tay; nguồn sự thật là ticket JSON -->";
const MAX_CELL_CHARS = 120;
const TABLE_HEAD = ["| id | kind | depends_on | title |", "| --- | --- | --- | --- |"];

/**
 * One line, redacted, clipped to 120 code points, then `|` escaped (clip first: never split `\|`).
 * Redaction is pattern only (no known-values): the same title already sits raw in
 * `.avatar/tickets/*.json` of the same commit, so this adds no exposure (review L5).
 */
function cell(text: string): string {
  const oneLine = redactEvidence(text, []).replace(/\s+/g, " ").trim();
  const clipped = Array.from(oneLine).slice(0, MAX_CELL_CHARS).join("");
  return clipped.replace(/\|/g, "\\|");
}

function byId(a: Ticket, b: Ticket): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function renderPlanIndexMarkdown(
  tickets: readonly Ticket[],
  ticketStatus: Record<string, string>,
): string {
  const listed = tickets
    .filter((t) => !isControllerReservedTicketId(t.id))
    .filter((t) => !RETIRED_TICKET_STATUSES.has(ticketStatus[t.id] ?? "todo"))
    .sort(byId);
  const slices = new Map<string, Ticket[]>();
  for (const t of listed) {
    const rows = slices.get(t.slice_id) ?? [];
    rows.push(t);
    slices.set(t.slice_id, rows);
  }
  const sections = [...slices].map(([sliceId, rows]) =>
    [
      `## ${cell(sliceId)}`,
      "",
      ...TABLE_HEAD,
      ...rows.map((t) => {
        const deps = t.depends_on.length === 0 ? "—" : [...t.depends_on].sort().join(", ");
        return `| ${cell(t.id)} | ${t.kind} | ${cell(deps)} | ${cell(t.title)} |`;
      }),
    ].join("\n"),
  );
  return `${[`${HEADER}\n# PLAN — mục lục ticket`, ...sections].join("\n\n")}\n`;
}

/** Writes the index at the fixed path (never a worker-supplied one), never through a symlink (06f R1). */
export async function writePlanIndex(
  repoDir: string,
  tickets: readonly Ticket[],
  ticketStatus: Record<string, string>,
): Promise<void> {
  await writeControllerWorktreeFile(
    repoDir,
    PLAN_INDEX_PATH,
    renderPlanIndexMarkdown(tickets, ticketStatus),
  );
}
